import request from '@/lib/request';
import { formatDataSourceHandle, fetchChartQuery, fetchStatisticValKey } from '@/lib/utils';
import { emptyColumnText } from '@/constant';

export async function batchGetDimensionData(infoArr, courseId) {
  const data = infoArr.map(item => {
    const { dataSource, componData, req_type: reqType } = item;
    const { rows, columns, statistics, relationFilterVal, relationFilterName } = componData;
    const relationStatisticItem = { value: relationFilterVal, statisticName: relationFilterName };
    let params = {};
    if (reqType === 'basic') {
      params = fetchDimensionBody({ rows, columns, statistics, dataSource, relationStatisticItem }, courseId);
    } else {
      params = fetchDimensionTableBody({ rows, columns, dataSource, relationStatisticItem }, courseId);
    }
    params.req_type = reqType;
    return params;
  });
  const result = await request.post('/v4/coursewares/analysis/chart/batch', { data });

  return result.data.map((item, index) => {
    const { componData, req_type: reqType } = infoArr[index];
    const { rows, columns, statistics, relationFilterVal, relationFilterName } = componData;
    const isBasic = reqType === 'basic';
    if (isBasic) {
      return formatDimensionData(item, {
        rows,
        columns,
        statistics,
        relationStatisticItem: { value: relationFilterVal, statisticName: relationFilterName }
      });
    } else {
      return formatDimensionTableData(item);
    }
  });
}

export function fetchDimensionBody({ rows, columns, statistics, dataSource, relationStatisticItem }, courseId) {
  const fields = fetchChartQuery({ rows, columns, statistics, relationStatisticItem });
  const bodyData = {
    ...formatDataSourceHandle(dataSource),
    fields,
    parentId: relationStatisticItem.value,
    courseware_id: courseId
  };
  // if (bodyData.parentId === 'full_crime_reason') {
  //   bodyData.parentId = 'defendantNames,full_crime_reason';
  // }
  return bodyData;
}

export function formatDimensionData(result, { rows = [], statistics = [], relationStatisticItem }) {
  let columnsData = [];
  let labelEnumObj = {};

  if (rows.length === 0) {
    if (statistics.length === 0) {
      const key = `${relationStatisticItem.value}(统计值)`;
      columnsData = [relationStatisticItem.statisticName];
      labelEnumObj[key] = { isStatistic: true, label: relationStatisticItem.statisticName };
    } else if (statistics.length === 1) {
      const statisticInfo = statistics[0];
      if (statisticInfo.title === '案件数') {
        const statisticTitle = statisticInfo.fieldDetail.title;
        const statisticKey = statisticInfo.fieldDetail.columnKey;
        columnsData = [`有${statisticTitle}`, `无${statisticTitle}`];
        labelEnumObj = {
          [`有${statisticKey}`]: { isStatistic: true, label: columnsData[0] },
          [`无${statisticKey}`]: { isStatistic: true, label: columnsData[1] }
        };
      } else {
        columnsData = [fetchStatisticValKey(statisticInfo, relationStatisticItem)];
        labelEnumObj = {
          [`${statisticInfo.fieldDetail.columnKey}(统计值)`]: { isStatistic: true, label: columnsData[0] }
        };
      }
    } else {
      statistics.forEach(statisticInfo => {
        const label = fetchStatisticValKey(statisticInfo, relationStatisticItem);
        columnsData.push(label);
        labelEnumObj[`${statisticInfo.fieldDetail.columnKey}(统计值)`] = { isStatistic: true, label };
      });
    }
  } else {
    rows.forEach(rowItem => {
      columnsData.push(rowItem.title);
      labelEnumObj[rowItem.columnKey] = {
        isField: true,
        label: rowItem.title
      };
    });
    if (statistics.length === 0) {
      columnsData.push(relationStatisticItem.statisticName);
      labelEnumObj[`${relationStatisticItem.value}(统计值)`] = {
        isStatistic: true,
        label: relationStatisticItem.statisticName
      };
    } else {
      statistics.forEach(statisticInfo => {
        const label = fetchStatisticValKey(statisticInfo, relationStatisticItem);
        columnsData.push(label);
        labelEnumObj[`${statisticInfo.fieldDetail.columnKey}(统计值)`] = { isStatistic: true, label };
      });
    }
  }
  const rowsData = (result.rows || []).map(rowItem => {
    const rowInfo = {};
    Object.keys(rowItem).forEach(key => {
      const labelData = labelEnumObj[key];
      const val = rowItem[key];
      if (!labelData) {
        rowInfo[key] = val;
        return;
      }

      if (labelData.isField) {
        rowInfo[labelData.label] = val || emptyColumnText;
      }
      if (labelData.isStatistic) {
        rowInfo[labelData.label] = Number(Number(val || 0).toFixed(2));
      }
    });
    return rowInfo;
  });
  return { rowsData, columnsData, totalData: result.total };
}

// 获取图表数据
export async function getDimension(
  { rows, columns, statistics, dataSource, relationFilterVal, relationFilterName },
  courseId = ''
) {
  let result;
  try {
    const relationStatisticItem = { value: relationFilterVal, statisticName: relationFilterName };
    const bodyData = fetchDimensionBody({ rows, columns, statistics, dataSource, relationStatisticItem }, courseId);
    result = await request.post('/v4/coursewares/analysis/chart', bodyData);
    result = formatDimensionData(result, { rows, columns, statistics, relationStatisticItem });
  } catch (err) {
    console.error(err);
  }

  return result || { rowsData: [], columnsData: [], totalData: {} };
}

export function fetchDimensionTableBody({ rows, columns, dataSource, relationStatisticItem }, courseId = '') {
  const x = rows.map(rowItem => rowItem.columnKey);
  const y = (columns[0] || {}).columnKey || '';
  const bodyData = {
    ...formatDataSourceHandle(dataSource),
    x,
    y,
    parentId: relationStatisticItem.value,
    courseware_id: courseId
  };
  // if (bodyData.parentId === 'full_crime_reason') {
  //   bodyData.parentId = 'defendantNames,full_crime_reason';
  // }
  return bodyData;
}

const defaultVal = { isNone: true };
export function formatDimensionTableData(result) {
  if (Object.keys(result || {}).length < 1) {
    return defaultVal;
  } else {
    return result;
  }
}

// 获取高级模型数据
export async function getDimensionTable({ rows, columns, dataSource, relationFilterVal }, courseId = '') {
  if (rows.length < 1 || columns.length < 1) {
    return defaultVal;
  }
  const bodyData = fetchDimensionTableBody(
    { rows, columns, dataSource, relationStatisticItem: { value: relationFilterVal } },
    courseId
  );
  const result = await request.post('/v4/coursewares/analysis/regression', bodyData);
  return formatDimensionTableData(result);
}
